
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../../config";
import { FullDetails } from "../../design-system/components/FullDetails";

type Status = "Scheduled" | "Pending" | "Completed";

interface UpcomingMatch {
  id: number;
  date: string;          // keep as string if your UI expects the page-formatted date
  time: string;
  opponent: string;
  division: string;
  yourElo: number;
  opponentElo: number;
  status: Status;
  matchLink?: string | null;
}

// You hard-coded a TEAM_ID in SidebarLayout originally.
// Duplicate it here or import from a constants file if you prefer.
const TEAM_ID = "0ee31a67-e505-436b-965b-adf5828f5d5a";

export function UpcomingMatches() {
  const [matches, setMatches] = useState<UpcomingMatch[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string | null>(null);

  // filters stay the same as your page had
  const [divisionFilter, setDivisionFilter] = useState<string>("All");
  const [statusFilter, setStatusFilter] = useState<string>("All");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr(null);
    fetch(apiUrl(`/api/faceit/schedule/${encodeURIComponent(TEAM_ID)}`))
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!mounted) return;
        if (!ok || j?.error) {
          setErr(j?.error || "Failed to load schedule");
          setMatches([]);
        } else {
          setMatches(j as UpcomingMatch[]);
        }
      })
      .catch(() => mounted && setErr("Network error"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return matches.filter((m) => {
      const divisionOk = divisionFilter === "All" || m.division === divisionFilter;
      const statusOk = statusFilter === "All" || m.status === statusFilter;
      return divisionOk && statusOk;
    });
  }, [matches, divisionFilter, statusFilter]);

  const groupedByDate = useMemo(() => {
    return filtered.reduce<Record<string, UpcomingMatch[]>>((acc, match) => {
      (acc[match.date] ||= []).push(match);
      return acc;
    }, {});
  }, [filtered]);

  const sortedDates = useMemo(
    () => Object.keys(groupedByDate).sort(),
    [groupedByDate]
  );

  if (loading) {
    return <div className="text-sm text-muted">Loading upcoming matches…</div>;
  }
  if (err) {
    return <div className="text-sm text-red-400">{err}</div>;
  }

  return (
    <>
      {/* HEADER */}
      <section>
        <h2>Opponent Schedule</h2>
        <p>
          External intelligence for upcoming matches, opponents, and prep status.
        </p>

        {/* Filters */}
        <select
          value={divisionFilter}
          onChange={(e) => setDivisionFilter(e.target.value)}
          className="bg-surface border border-border rounded-md p-2 focus:outline-none focus:border-[#4FC3F7]"
        >
          <option>All</option>
          <option>Advanced</option>
          <option>Main</option>
          <option>Intermediate</option>
          <option>Open</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-surface border border-border rounded-md p-2 focus:outline-none focus:border-[#4FC3F7]"
        >
          <option>All</option>
          <option>Scheduled</option>
          <option>Pending</option>
          <option>Completed</option>
        </select>
      </section>

      {/* GROUPED BY DATE */}
      {sortedDates.length === 0 && (
        <div>No matches found with current filters.</div>
      )}

      {sortedDates.map((date) => (
        <section key={date}>
          {/* Date header */}
          <h3>
            {new Date(date).toLocaleDateString(undefined, {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}
          </h3>
          <div>
            {(groupedByDate[date]?.length ?? 0)} match
            {(groupedByDate[date]?.length ?? 0) > 1 ? "es" : ""}
          </div>

          {/* Match cards */}
          {(groupedByDate[date] ?? []).map((match) => {
            const delta = (match.opponentElo ?? 0) - (match.yourElo ?? 0);
            const deltaSign = delta >= 0 ? "+" : "";
            return (
              <div key={match.id}>
                {/* Top row */}
                <div>Opponent</div>
                <div>{match.opponent}</div>
                <div>{match.division}</div>
                <div>Time</div>
                <div>{match.time}</div>

                {/* ELO comparison */}
                <div>Your ELO</div>
                <div>{match.yourElo ?? 0}</div>
                <div>Opponent ELO</div>
                <div>{match.opponentElo ?? 0}</div>
                <div>Delta</div>
                <div>
                  {deltaSign}
                  {delta}
                </div>

                {/* Status */}
                <div>{match.status} Upcoming</div>

                {/* Actions */}
                <div>
                  <button>Open Match Prep</button>
                  <button>Opponent Scouting</button>
                  <button>Team Profile</button>
                  {match.matchLink && (
                    <a href={match.matchLink}>[ View Match Page ]</a>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      ))}

      <FullDetails data={matches} title="All fields" />
    </>
  );
}
